import { useEffect, useState } from "react";
import { Phone, Menu, X, Instagram, Facebook } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BUSINESS, LOGO_URL } from "@/lib/siteData";

const links = [
  { href: "/#about", label: "About" },
  { href: "/#services", label: "Services" },
  { href: "/#gallery", label: "Gallery" },
  { href: "/#reviews", label: "Reviews" },
  { href: "/#blog", label: "Blog" },
  { href: "/#contact", label: "Contact" },
];

export const NavBar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-md border-b border-green-100 shadow-sm"
          : "bg-gradient-to-b from-green-950/40 to-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-brand-link"
          className={`flex items-center gap-3 font-bold ${
            scrolled ? "text-green-900" : "text-white"
          }`}
        >
          <span className="inline-flex items-center justify-center rounded-full overflow-hidden w-11 h-11 bg-white shadow-md ring-1 ring-green-100">
            <img
              src={LOGO_URL}
              alt="Pond & Garden Maintenance Services logo"
              className="w-11 h-11 object-cover"
            />
          </span>
          <span className="hidden sm:flex flex-col leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            <span className="text-base sm:text-lg font-extrabold">Pond &amp; Garden</span>
            <span className={`text-[10px] font-medium tracking-widest uppercase ${scrolled ? "text-green-600" : "text-white/70"}`}>
              Maintenance Services
            </span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-sm font-medium transition-colors ${
                scrolled
                  ? "text-green-900/80 hover:text-green-700"
                  : "text-white/90 hover:text-white"
              }`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={BUSINESS.social.facebook}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Facebook"
            data-testid="nav-facebook-link"
            className={`inline-flex items-center justify-center w-10 h-10 rounded-full transition-colors ${
              scrolled
                ? "text-green-700 hover:bg-green-50"
                : "text-white hover:bg-white/15"
            }`}
          >
            <Facebook className="w-5 h-5" />
          </a>
          <a
            href={BUSINESS.social.instagram}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
            data-testid="nav-instagram-link"
            className={`inline-flex items-center justify-center w-10 h-10 rounded-full transition-colors ${
              scrolled
                ? "text-green-700 hover:bg-green-50"
                : "text-white hover:bg-white/15"
            }`}
          >
            <Instagram className="w-5 h-5" />
          </a>
          <a href={`tel:${BUSINESS.phoneTel}`} data-testid="nav-call-link">
            <Button
              size="sm"
              className="bg-green-700 hover:bg-green-800 text-white rounded-full px-5"
              data-testid="nav-call-button"
            >
              <Phone className="w-4 h-4 mr-2" />
              {BUSINESS.phone}
            </Button>
          </a>
        </div>

        <button
          type="button"
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className={`md:hidden p-2 rounded-md ${scrolled ? "text-green-900" : "text-white"}`}
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="md:hidden bg-white border-t border-green-100 shadow-lg"
        >
          <div className="px-4 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                className="text-green-900 font-medium py-2 border-b border-green-50"
              >
                {l.label}
              </a>
            ))}
            <a href={`tel:${BUSINESS.phoneTel}`} data-testid="nav-mobile-call" className="mt-2">
              <Button className="w-full bg-green-700 hover:bg-green-800 rounded-full">
                <Phone className="w-4 h-4 mr-2" /> {BUSINESS.phone}
              </Button>
            </a>
            <a
              href={BUSINESS.social.instagram}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-instagram"
              className="flex items-center justify-center gap-2 mt-1 py-2 text-green-800 font-medium"
            >
              <Instagram className="w-5 h-5" /> Follow us on Instagram
            </a>
            <a
              href={BUSINESS.social.facebook}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-facebook"
              className="flex items-center justify-center gap-2 py-2 text-green-800 font-medium"
            >
              <Facebook className="w-5 h-5" /> Find us on Facebook
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default NavBar;
